import { LoyaltyServiceRewardType } from 'generated/graphql-gateway';
import {
  IEngineRewardsMap,
  ISanityRewardsMap,
  LoyaltyEngineReward,
  LoyaltyReward,
} from 'state/loyalty/types';

import { ServiceReward, ServiceRewardCategory } from '../types';

export function toBlockContent(value: unknown): { localeRaw: any[] } {
  if (!value) {
    return { localeRaw: [] };
  }
  if (typeof value === 'object' && !Array.isArray(value) && (value as any).localeRaw) {
    const raw = (value as any).localeRaw;
    return { localeRaw: Array.isArray(raw) ? raw : [] };
  }
  if (Array.isArray(value)) {
    return { localeRaw: value };
  }
  if (typeof value === 'string') {
    return { localeRaw: [{ _type: 'block', children: [{ _type: 'span', text: value }] }] };
  }
  return { localeRaw: [] };
}

function toLegacyBlockContent(value: unknown): any[] {
  if (!value) {
    return [];
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    const record = value as Record<string, unknown>;
    if (Array.isArray(record.localeRaw)) {
      return record.localeRaw;
    }
    if (Array.isArray(record.enRaw)) {
      return record.enRaw;
    }
    if (Array.isArray(record.locale)) {
      return record.locale;
    }
  }
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === 'string') {
    return [{ _type: 'block', children: [{ _type: 'span', text: value }] }];
  }
  return [];
}

function toLegacyDescription(value: unknown): { locale: any[] } | undefined {
  const blocks = toLegacyBlockContent(value);
  return blocks.length ? { locale: blocks } : undefined;
}

function toLegacyEnRaw(value: unknown): { enRaw: any[] } | undefined {
  const blocks = toLegacyBlockContent(value);
  return blocks.length ? { enRaw: blocks } : undefined;
}

function toLocaleString(value: unknown): { locale: string } {
  if (!value) {
    return { locale: '' };
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    const maybeLocale = (value as any).locale;
    if (typeof maybeLocale === 'string') {
      return { locale: maybeLocale };
    }
    const maybeEn = (value as any).en;
    if (typeof maybeEn === 'string') {
      return { locale: maybeEn };
    }
    const maybeLocaleRaw = toLegacyBlockContent((value as any).localeRaw);
    if (maybeLocaleRaw.length) {
      return {
        locale: maybeLocaleRaw
          .map(block =>
            Array.isArray(block.children)
              ? block.children.map((child: { text?: string }) => child.text ?? '').join('')
              : ''
          )
          .join(' ')
          .trim(),
      };
    }
  }
  if (typeof value === 'string') {
    return { locale: value };
  }
  return { locale: '' };
}

function toRewardCardImage(value: unknown): unknown {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return value ?? undefined;
  }

  const imageRecord = value as Record<string, unknown>;
  const locale = imageRecord.locale;
  if (!locale || typeof locale !== 'object' || Array.isArray(locale)) {
    return value;
  }

  const localeRecord = locale as Record<string, unknown>;
  return 'app' in localeRecord ? { ...imageRecord, locale: localeRecord.app ?? null } : value;
}

/**
 * Convert a `LoyaltyServiceReward` into a Sanity-flavored `LoyaltyReward`
 * so that presentational components (IncentiveCard, LoyaltyRewardsCategory,
 * etc.) can keep consuming the existing Sanity-shaped interface.
 */
export function mapServiceReward(reward: ServiceReward): LoyaltyReward {
  const renderable = (reward.renderable as any) ?? {};
  const isConfigReward = reward.type === LoyaltyServiceRewardType.CONFIG;

  return ({
    _id: reward.sanityId ?? reward.cmsId ?? reward.id,
    _type: isConfigReward ? 'configReward' : 'reward',
    __typename: isConfigReward ? 'ConfigReward' : 'Reward',
    cmsId: reward.cmsId ?? reward.sanityId ?? reward.id,
    configId: reward.configId ?? undefined,
    loyaltyEngineId: reward.id,
    sanityId: reward.sanityId ?? undefined,
    loyaltyId: reward.loyaltyId ?? undefined,

    type: reward.type,
    mobileOrderOnly: reward.mobileOrderOnly ?? undefined,
    incentiveNotInMenu: reward.incentiveNotInMenu ?? undefined,

    rewardBenefitId: reward.rewardBenefitId ?? undefined,
    pointCost: reward.pointCost,
    locked: reward.locked ?? undefined,
    redemptionPercentage: reward.redemptionPercentage ?? undefined,
    limitPerOrder: reward.limitPerOrder ?? undefined,
    remainingPointsNeededForRedemption: reward.remainingPointsNeededForRedemption ?? undefined,

    errors: reward.errors ?? undefined,
    metadata: reward.metadata ?? undefined,
    incentives: reward.incentives ?? undefined,
    vendorConfigs: reward.vendorConfigs ?? undefined,

    rank: reward.rank ?? undefined,
    startDate: reward.startDate ?? undefined,
    endDate: reward.endDate ?? undefined,

    inlineAlertMessage:
      (renderable.inlineAlertMessage as string) ?? reward.inlineAlertMessage ?? undefined,

    name: toLocaleString(renderable.name ?? reward.name),
    description: toLegacyDescription(renderable.description ?? reward.description),
    moreInfo: toLegacyEnRaw(renderable.moreInfo ?? reward.moreInfo),
    howToRedeem: toLegacyEnRaw(renderable.howToRedeem ?? reward.howToRedeem),
    imageDescription: toLocaleString(renderable.imageDescription ?? reward.imageDescription),
    image: toRewardCardImage(renderable.image ?? reward.image),
    localizedImage: renderable.localizedImage ?? reward.localizedImage ?? undefined,
    backgroundImage: renderable.backgroundImage ?? reward.backgroundImage ?? undefined,
  } as unknown) as LoyaltyReward;
}

/**
 * Convert a `LoyaltyServiceRewardCategory` into a Sanity-flavored
 * `NonNullableRewardCategory` by wrapping the flat `label` string in a
 * locale object and mapping each reward entry.
 */
export function mapServiceRewardCategory(
  category: ServiceRewardCategory
): {
  category: any;
  sanityRewards: LoyaltyReward[];
  engineEntries: Array<{ engineId: string; engineReward: LoyaltyEngineReward }>;
} {
  const sanityRewards = category.rewards.map(mapServiceReward);

  const engineEntries = category.rewards.map(reward => ({
    engineId: reward.id,
    engineReward: ({
      __typename: 'LoyaltyReward',
      id: reward.id,
      rewardBenefitId: reward.rewardBenefitId ?? '',
      name: reward.name ?? '',
      pointCost: reward.pointCost,
      sanityId: reward.sanityId ?? null,
      locked: reward.locked ?? false,
      redemptionPercentage: reward.redemptionPercentage ?? null,
      limitPerOrder: reward.limitPerOrder ?? null,
      remainingPointsNeededForRedemption: reward.remainingPointsNeededForRedemption ?? null,
      type: (reward.type as unknown) as LoyaltyEngineReward['type'],
      errors: reward.errors ?? null,
    } as unknown) as LoyaltyEngineReward,
  }));

  return {
    category: {
      __typename: 'RewardCategory',
      _id: category.label,
      label: { __typename: 'LocaleString', en: category.label },
      image: category.image ?? null,
      imageDescription: category.imageDescription
        ? { __typename: 'LocaleString', locale: category.imageDescription }
        : null,
      rewards: sanityRewards,
    },
    sanityRewards,
    engineEntries,
  };
}

export interface IMappedRewardCategories {
  rewardCategories: any[];
  sanityRewardsMap: ISanityRewardsMap;
  engineRewardsMap: IEngineRewardsMap;
}

export function mapServiceRewardCategories(
  categories: ReadonlyArray<ServiceRewardCategory>
): IMappedRewardCategories {
  const rewardCategories: any[] = [];
  const sanityRewardsMap: ISanityRewardsMap = {};
  const engineRewardsMap: IEngineRewardsMap = {};

  for (const serviceCategory of categories) {
    const { category, sanityRewards, engineEntries } = mapServiceRewardCategory(serviceCategory);
    rewardCategories.push(category);

    for (const sanityReward of sanityRewards) {
      sanityRewardsMap[sanityReward._id] = sanityReward;
    }
    for (const { engineId, engineReward } of engineEntries) {
      engineRewardsMap[engineId] = engineReward;
    }
  }

  return { rewardCategories, sanityRewardsMap, engineRewardsMap };
}
